import type { UserRole } from "@template/shared";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface RoleImpersonationState {
  /** The role an admin is currently viewing the UI as. `null` = not impersonating. */
  impersonatedRole: UserRole | null;
  setImpersonatedRole: (role: UserRole | null) => void;
  clear: () => void;
}

/**
 * Admin DX helper: lets an administrator dim the UI to preview what a
 * lower-privilege role would see. Persisted to localStorage so a refresh
 * does not silently drop back into admin view.
 *
 * CRITICAL: this is UI-level only. The API still authorises requests
 * against the REAL cookie-backed role. If the impersonating admin calls
 * an endpoint their "viewed-as" role lacks, the request still succeeds
 * because the backend trusts only the signed cookie. Impersonation lets
 * admins verify *what buttons are visible*, not *what permissions exist*.
 */
export const useRoleImpersonationStore = create<RoleImpersonationState>()(
  persist(
    (set) => ({
      impersonatedRole: null,
      setImpersonatedRole: (role) => set({ impersonatedRole: role }),
      clear: () => set({ impersonatedRole: null }),
    }),
    { name: "rbac-impersonation" },
  ),
);
